import { useEffect, useId, useRef, useState } from 'react';
import { defaultNextStepDue, eventName, meetingTitle, type SyncOp } from '@wr/shared';
import { errorMessage } from '../../api.js';
import {
  ContactForm,
  EMPTY_CONTACT,
  toFieldErrors,
  type ContactDraft,
  type FieldErrors,
} from '../../components/ContactForm.js';
import type { LinkItem } from '../../components/LinkPicker.js';
import { RecordSearch } from '../../components/RecordSearch.js';
import { useToast } from '../../components/Toast.js';
import { Dialog, Field, Notice, TextAreaField } from '../../components/ui.js';
import { validateContact } from '../../lists.js';
import { useOffline } from '../../offline/context.js';
import { newId } from '../../offline/ids.js';
import { useSignedIn } from '../../session.js';
import { importHubSpotContact } from '../leads/data.js';
import {
  KnownContact,
  ProjectBlock,
  focusFirst,
  knownContactId,
  leadOps,
  projectErrors,
  projectFrom,
  useKnownContact,
  type ProjectDraft,
  type ProjectErrors,
} from '../leads/Project.js';
import { useStages } from '../leads/stages.js';
import { useAskEvent } from '../capture/AskEvent.js';
import { localNow, localToday, useColleagues } from './data.js';

/** Someone met: a contact of the portal, or a new one typed here (block 1). */
interface Person {
  key: string;
  id: string;
  label: string;
  company: string | null;
  country: string | null;
  /** New: created with the meeting. */
  draft?: ContactDraft;
}

const str = (v: unknown) => (typeof v === 'string' ? v : '');

/**
 * Document a meeting (decision of Nov 3, 2026): with whom (one or more
 * contacts: found in the portal or HubSpot, or new with block 1), the lead
 * it is about (optional: found, or new with block 2), when (now by
 * default), who of Waste Robotics was there (the person by default), notes,
 * and a next step that becomes a HubSpot task. The current event is set by
 * itself. Saved on the device first, as every capture; one Save.
 */
export function MeetingForm({
  people: initialPeople = [],
  lead: initialLead = null,
  onClose,
  onSaved,
}: {
  people?: { id: string; label: string; company: string | null; country?: string | null }[];
  lead?: { id: string; label: string } | null;
  onClose(): void;
  onSaved(): void;
}) {
  const { user, can } = useSignedIn();
  const { save, store, currentEventId } = useOffline();
  const askEvent = useAskEvent();
  const toast = useToast();
  const stages = useStages();
  const colleagues = useColleagues();
  const [people, setPeople] = useState<Person[]>(
    initialPeople.map((p) => ({ key: p.id, country: null, ...p })),
  );
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<ContactDraft>(EMPTY_CONTACT);
  const [draftErrors, setDraftErrors] = useState<FieldErrors>({});
  const [moreOpen, setMoreOpen] = useState(false);
  const known = useKnownContact(adding ? draft.email : '');
  const [lead, setLead] = useState<{ id: string; label: string } | null>(initialLead);
  const [newLead, setNewLead] = useState(false);
  const [answers, setAnswers] = useState<ProjectDraft>(() => projectFrom());
  const [projectErrs, setProjectErrs] = useState<ProjectErrors>({});
  const [when, setWhen] = useState(localNow());
  const [attendees, setAttendees] = useState<string[]>([user.id]);
  const [notes, setNotes] = useState('');
  // Nov 4, 2026: the title suggested (with whom, the show) until the seller changes it.
  const [title, setTitle] = useState<string | null>(null);
  const [nextStep, setNextStep] = useState('');
  const [nextDue, setNextDue] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Nov 4, 2026: one meeting per form, whatever the touches. Its ID is chosen once, so
  // Save touched twice, or again after a problem, sends the same meeting, never a second.
  const [meetingId] = useState(newId);
  const busy = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const formId = useId();
  // The next step's date by default: three working days after the show's last day.
  const [eventEnds, setEventEnds] = useState<string | null>(null);
  const [showName, setShowName] = useState<string | null>(null);
  useEffect(() => {
    let current = true;
    void currentEventId().then(async (id) => {
      const event = id ? await store?.getRecord('event', id) : null;
      if (!current) return;
      setEventEnds(str(event?.ends_on).slice(0, 10) || null);
      setShowName(event ? eventName(event) || null : null);
    });
    return () => {
      current = false;
    };
  }, [currentEventId, store]);

  /** A contact found: one of the portal, or one of HubSpot imported first (never twice). */
  async function pick(item: LinkItem) {
    setProblem(null);
    try {
      const id = item.id ?? (item.hubspotId ? await importHubSpotContact(item.hubspotId) : null);
      if (!id || people.some((p) => p.id === id)) return;
      setPeople((list) => [
        ...list,
        { key: id, id, label: item.label, company: item.company, country: null },
      ]);
    } catch (error) {
      setProblem(errorMessage(error));
    }
  }

  /** The person typed (block 1) joins the meeting; created at Save. */
  function addNew() {
    const result = validateContact(draft);
    if (!result.ok) {
      setDraftErrors(toFieldErrors(result.errors));
      return;
    }
    const id = newId();
    setPeople((list) => [
      ...list,
      {
        key: id,
        id,
        label: `${result.value.first_name} ${result.value.last_name}`,
        company: result.value.company,
        country: result.value.country ?? null,
        draft,
      },
    ]);
    setDraft(EMPTY_CONTACT);
    setDraftErrors({});
    setAdding(false);
  }

  /** "Meeting with <company, else name> (<show>)"; without a show, no brackets. */
  const suggestedTitle = (show: string | null) =>
    meetingTitle(people[0] ? people[0].company || people[0].label : null, show);

  async function submit() {
    if (busy.current) return;
    setProblem(null);
    if (people.length === 0) {
      setProblem('Add the person you met: a contact found, or a new one.');
      return;
    }
    const at = new Date(when);
    if (Number.isNaN(at.getTime())) {
      setProblem('When: a date and a time.');
      return;
    }
    // A meeting documents what took place (Nov 4, 2026); the next step plans what follows.
    if (at.getTime() > Date.now() + 60_000) {
      setProblem('When: a meeting that took place. Plan what follows as the next step.');
      return;
    }
    if (newLead) {
      const errs = projectErrors(answers);
      setProjectErrs(errs ?? {});
      if (errs) return focusFirst(rootRef.current, errs);
    }
    busy.current = true;
    setSaving(true);
    try {
      // During a show of the person's team with none picked: asked first (Nov 3, 2026).
      const eventId = await askEvent.eventForCapture();
      if (eventId === undefined) {
        busy.current = false;
        return setSaving(false);
      }
      const changedAt = new Date().toISOString();
      const event = eventId ? await store?.getRecord('event', eventId) : null;
      const ops: SyncOp[] = [];
      for (const p of people) {
        if (!p.draft) continue;
        const values = validateContact(p.draft);
        if (!values.ok) continue;
        ops.push({
          opId: newId(),
          entity: 'contact',
          id: p.id,
          action: 'upsert',
          changedAt,
          fields: {
            ...Object.fromEntries(
              Object.entries(values.value).filter(
                ([, v]) => v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0),
              ),
            ),
            source: 'manual',
            event_id: eventId,
          },
        });
      }
      let leadId = lead?.id ?? null;
      if (newLead) {
        const first = people[0]!;
        const created = leadOps({
          person: {
            id: first.id,
            company: first.company,
            country: first.country,
            event_id: eventId,
          },
          draft: answers,
          eventId,
          eventCode: str(event?.event_code) || null,
          ownerId: user.id,
          qualify: can('leads.qualify'),
          stages,
          changedAt,
        });
        ops.push(...created.ops);
        leadId = created.leadId;
      }
      const due =
        nextStep.trim() === ''
          ? null
          : (nextDue ?? defaultNextStepDue(str(event?.ends_on).slice(0, 10) || localToday()));
      ops.push({
        opId: newId(),
        entity: 'meeting',
        id: meetingId,
        action: 'upsert',
        changedAt,
        fields: {
          event_id: eventId,
          met_at: at.toISOString(),
          contact_ids: people.map((p) => p.id),
          attendee_ids: attendees.length > 0 ? attendees : [user.id],
          lead_id: leadId,
          notes: notes.trim() || null,
          next_step: nextStep.trim() || null,
          next_step_due: due,
          // Not changed by the seller: suggested again with the show it is saved for.
          title: (title ?? '').trim() || suggestedTitle(event ? eventName(event) || null : null),
        },
      });
      await save(...ops);
      toast('Meeting saved. It goes to HubSpot by itself.');
      onSaved();
    } catch {
      setProblem('This device could not save the meeting. Nothing was lost: try again.');
      busy.current = false;
      setSaving(false);
    }
  }

  const shownDue = nextDue ?? defaultNextStepDue(eventEnds ?? localToday());

  return (
    <Dialog
      title="Meeting"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn primary" disabled={saving}>
            Save
          </button>
        </>
      }
    >
      <form
        id={formId}
        className="dlg-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div ref={rootRef} className="dlg-form">
          <div className="formsec" role="group" aria-label="With whom">
            <h3>With whom</h3>
            {people.length > 0 && (
              <div className="chips">
                {people.map((p) => (
                  <span key={p.key} className="tchip">
                    {p.label}
                    {p.company ? `, ${p.company}` : ''}
                    {p.draft ? ' (new)' : ''}
                    <button
                      type="button"
                      aria-label={`Remove ${p.label}`}
                      onClick={() => setPeople((list) => list.filter((x) => x.key !== p.key))}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            {!adding && (
              <>
                <RecordSearch
                  label="Find a contact"
                  inline
                  placeholder="Name, company or email"
                  kinds={['contact']}
                  exclude={people.map((p) => p.id)}
                  onPick={(item) => void pick(item)}
                />
                <button type="button" className="linkbtn" onClick={() => setAdding(true)}>
                  New person
                </button>
              </>
            )}
            {adding && (
              <>
                <ContactForm
                  value={draft}
                  onChange={(v) => {
                    setDraft(v);
                    if (Object.keys(draftErrors).length > 0) setDraftErrors({});
                  }}
                  errors={draftErrors}
                  moreOpen={moreOpen}
                  onMoreToggle={setMoreOpen}
                  leadAnswers={false}
                >
                  {known && (
                    <KnownContact
                      known={known}
                      what="Add this contact instead of a second one."
                      onOpen={(_lead) =>
                        void knownContactId(known).then((id) => {
                          if (!id || people.some((p) => p.id === id)) return;
                          setPeople((list) => [
                            ...list,
                            {
                              key: id,
                              id,
                              label: known.label,
                              company: known.company,
                              country: null,
                            },
                          ]);
                          setDraft(EMPTY_CONTACT);
                          setAdding(false);
                        })
                      }
                    />
                  )}
                </ContactForm>
                <div className="actions">
                  <button
                    type="button"
                    className="btn sm primary"
                    disabled={Boolean(known)}
                    onClick={addNew}
                  >
                    Add this person
                  </button>
                  <button
                    type="button"
                    className="btn sm"
                    onClick={() => {
                      setAdding(false);
                      setDraft(EMPTY_CONTACT);
                      setDraftErrors({});
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="formsec" role="group" aria-label="Lead">
            <h3>Lead (optional)</h3>
            {lead && !newLead && (
              <p style={{ margin: 0 }}>
                <b>{lead.label}</b>{' '}
                <button type="button" className="linkbtn" onClick={() => setLead(null)}>
                  Remove
                </button>
              </p>
            )}
            {!lead && !newLead && (
              <>
                <RecordSearch
                  label="Link to a lead"
                  inline
                  placeholder="Company or lead name"
                  kinds={['lead']}
                  onPick={(item) => item.id && setLead({ id: item.id, label: item.label })}
                />
                {can('leads.create') && (
                  <button
                    type="button"
                    className="linkbtn"
                    disabled={people.length === 0}
                    onClick={() => setNewLead(true)}
                  >
                    New project
                  </button>
                )}
              </>
            )}
            {newLead && (
              <>
                <ProjectBlock
                  value={answers}
                  onChange={(v) => {
                    setAnswers(v);
                    setProjectErrs({});
                  }}
                  errors={projectErrs}
                  personName={people[0]?.label.split(' ')[0]}
                />
                <button type="button" className="linkbtn" onClick={() => setNewLead(false)}>
                  No new project
                </button>
              </>
            )}
          </div>

          <Field
            label="When"
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
          />

          <div className="fm-q" role="group" aria-label="Waste Robotics">
            <b>Waste Robotics</b>
            <div className="chips">
              {colleagues.map((c) => {
                const on = attendees.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`le-chip${on ? ' on' : ''}`}
                    aria-pressed={on}
                    onClick={() =>
                      setAttendees((list) =>
                        on ? list.filter((id) => id !== c.id) : [...list, c.id],
                      )
                    }
                  >
                    {c.name}
                  </button>
                );
              })}
            </div>
          </div>

          <Field
            label="Title (optional)"
            hint="The meeting's title in HubSpot"
            value={title ?? suggestedTitle(showName)}
            maxLength={250}
            onChange={(e) => setTitle(e.target.value)}
          />
          <TextAreaField
            label="Notes"
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <Field
            label="Next step (optional)"
            hint="Becomes a HubSpot task"
            value={nextStep}
            onChange={(e) => setNextStep(e.target.value)}
          />
          {nextStep.trim() !== '' && (
            <Field
              label="Next step date"
              type="date"
              value={shownDue}
              onChange={(e) => setNextDue(e.target.value || null)}
            />
          )}
          {problem && <Notice kind="error">{problem}</Notice>}
        </div>
      </form>
      {askEvent.dialog}
    </Dialog>
  );
}
